import { AfterViewInit, Directive, ElementRef, EmbeddedViewRef, OnDestroy, OnInit, TemplateRef, ViewContainerRef, inject } from '@angular/core';

/**
 * Renders its content into a LIGHT-DOM node that is projected back into the shadow layout through
 * a `<slot>`.
 *
 * Payment SDKs resolve their mount container through `document` — Stripe.js looks the element up
 * itself, and `ngx-paypal` hands the PayPal SDK a document-level CSS selector
 * (`paypal.Buttons(config).render('#' + containerId)`). Neither can see into a shadow tree, so
 * inside the Shadow-DOM web component the Payment Element emits `loaderstart` and never `ready`,
 * and the PayPal button simply never appears. Both fail SILENTLY — no error event, nothing in the
 * console, nothing in Sentry.
 *
 * A slotted node is the way out: it is a direct child of the shadow host, so it physically lives
 * in the light DOM and `document` queries find it, while rendering at the `<slot>` position inside
 * the widget's layout. Only the mount container crosses the boundary — the skeletons, the error
 * banners and everything else stay inside the shadow root.
 *
 * Two details that are easy to get wrong, both load-bearing:
 *
 * 1. The view is created through the **ViewContainerRef**, so it is inserted into the container
 *    this directive is declared in and the host component's `@ViewChild` finds it —
 *    `collectQueryResults` walks that declaration container. `TemplateRef.createEmbeddedView()`
 *    plus `ApplicationRef.attachView()` resolves the query too (`attachToAppRef` calls
 *    `trackMovedView`, which populates the container's `MOVED_VIEWS`, and `collectQueryResults`
 *    walks that as well) — the widget repo's equivalent fix takes that route. Either is correct;
 *    this one is simply the API a structural directive already holds. What matters is that the
 *    view is reachable from the declaration container at all — that query is what
 *    `confirmStripePayment()` dereferences, so a view created outside both routes would render
 *    the form perfectly and then fail at the moment of paying. Moving the DOM nodes afterwards is
 *    safe either way: it does not move the view in Angular's tree.
 *
 * 2. Outside a shadow root the content is left exactly where it rendered. There is no boundary to
 *    cross, and relocating it would move it out of whatever wrapper the host template uses to hide
 *    it while loading.
 */
@Directive({
  selector: '[calioLightDomProjection]',
  standalone: true,
})
export class LightDomProjectionDirective implements OnInit, AfterViewInit, OnDestroy {

  /** Unique per instance so two payment surfaces can never project into the same slot. */
  private static sequence = 0;

  /** Also the hook the shadow root and the document-level guard style against. */
  static readonly SLOT_CLASS = 'calio-light-dom-slot';

  private readonly slotName = `calio-light-dom-${LightDomProjectionDirective.sequence++}`;

  private templateRef = inject<TemplateRef<unknown>>(TemplateRef);
  private viewContainer = inject(ViewContainerRef);
  private anchor = inject<ElementRef<Comment>>(ElementRef);

  private view?: EmbeddedViewRef<unknown>;
  private slotHost?: HTMLElement;
  private slotElement?: HTMLElement;

  private static readonly GUARD_STYLE_ID = 'calio-light-dom-slot-guard';

  /**
   * `::slotted()` hardens the projected wrapper but cannot reach its DESCENDANTS, so a host-page
   * rule like `.x * { opacity: 0 }` could still blank the provider's iframe. Both SDKs nest
   * several wrappers between the slot host and the iframe, so the guard has to cover every
   * descendant. It pins only what could HIDE or DISABLE the form - never layout properties the
   * SDKs size themselves.
   *
   * The class is doubled on purpose: an equal-specificity `!important` rule would otherwise win
   * simply by coming later in the document, which a host page can always arrange. Doubling it
   * raises specificity to (0,2,0), so out-specifying it takes deliberate effort - and only breaks
   * that page's own payment form.
   */
  private static installHostPageGuard(): void {
    if (document.getElementById(LightDomProjectionDirective.GUARD_STYLE_ID)) {
      return;
    }
    const cls = LightDomProjectionDirective.SLOT_CLASS;
    const style = document.createElement('style');
    style.id = LightDomProjectionDirective.GUARD_STYLE_ID;
    style.textContent = `
      .${cls}.${cls},
      .${cls}.${cls} * {
        opacity: 1 !important;
        visibility: inherit !important;
        filter: none !important;
        transform: none !important;
        pointer-events: auto !important;
        max-height: none !important;
        clip-path: none !important;
      }`;
    document.head.appendChild(style);
  }

  ngOnInit(): void {
    // Created through the ViewContainerRef (see note 1 above) - this is what keeps the host
    // component's @ViewChild resolving.
    this.view = this.viewContainer.createEmbeddedView(this.templateRef);
    this.projectIfInsideShadowRoot();
  }

  /**
   * Second attempt, because the FIRST one is timing-dependent and its failure is silent.
   * `getRootNode()` only reports the shadow root once the host element is attached to it; if this
   * directive initialises while the subtree is still detached it reads a DocumentFragment, takes
   * the light-DOM path, and leaves the payment mount inside the shadow root - which is the exact
   * bug this directive exists to prevent, reintroduced with no signal. Retrying after view init
   * costs nothing when the first attempt already worked (it is a no-op) and is well ahead of
   * either SDK, since both only mount after an async round-trip.
   */
  ngAfterViewInit(): void {
    this.projectIfInsideShadowRoot();
  }

  private projectIfInsideShadowRoot(): void {
    if (this.slotHost) {
      return;
    }
    const root = this.anchor.nativeElement.getRootNode();
    if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) {
      return;
    }

    LightDomProjectionDirective.installHostPageGuard();

    const slotHost = document.createElement('div');
    slotHost.setAttribute('slot', this.slotName);
    slotHost.className = LightDomProjectionDirective.SLOT_CLASS;
    (root.host as HTMLElement).appendChild(slotHost);
    this.slotHost = slotHost;

    // The <slot> marks where the projected content renders, so the layout is identical to
    // rendering in place.
    const slot = document.createElement('slot');
    slot.setAttribute('name', this.slotName);
    this.anchor.nativeElement.parentNode?.insertBefore(slot, this.anchor.nativeElement);
    this.slotElement = slot;

    // Moving the ROOT nodes (including the anchor comments of any @if inside) is what makes this
    // hold for content that appears later: Angular inserts around those comments, so a block that
    // renders once the client secret arrives lands in the slot host too.
    this.view?.rootNodes.forEach((node: Node) => slotHost.appendChild(node));
  }

  ngOnDestroy(): void {
    this.slotHost?.remove();
    this.slotHost = undefined;
    this.slotElement?.remove();
    this.slotElement = undefined;
  }
}
